<template>
    <div v-cloak class="imageVideoGallery" :class="{ 'is-grid-mode': layoutMode == 'grid', 'is-loading': loading, 'is-modern': galleryVersion == 'modern', 'is-legacy': galleryVersion == 'legacy'}">
        <div class="loading-icon" aria-label="Loading" :aria-hidden="loading == false"><div></div><div></div></div>
        <div class="group">
            <div class="componentContainer">
                <div class="controls-container" :class="{'controls-container--modern' : (galleryVersion == 'modern') }">
                    <template v-if="galleryVersion == 'legacy'">
                        <div class="controls-container__inner">
                            <gallery-filter v-if="showFilterControls" />
                            <gallery-autoplay-toggle />
                        </div>
                    </template>
                    <template v-else-if="(galleryVersion == 'modern' && pageWidth >= 768)">  <!-- Modern desktop -->
                        <galleryLayoutModeToggle class="controls-container__inner--modern" />

                        <div class="controls-container__inner">
                            <gallery-filter v-if="showFilterControls" />
                            <gallery-autoplay-toggle />
                        </div>
                    </template>
                    <template v-else-if="(galleryVersion == 'modern' && pageWidth < 768)"><!-- Modern mobile -->
                        <div class="controls-container__inner controls-container__inner--modern mobile-show">
                            <div class="controls-container__inner-top">
                                <galleryLayoutModeToggle />
                                <gallery-autoplay-toggle class="controls-container__inner--modern"/>
                            </div>
                            <div class="controls-container__inner-bottom">
                                <gallery-filter v-if="showFilterControls" />
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <div class="componentContainer componentContainer--listing">
                <gallery-list />
            </div>
        </div>

        <transition name="fade">
            <gallery-modal v-if="activeItem" />
        </transition>
    </div>
</template>

<script>
    import galleryFilter from './galleryFilter.vue';
    import galleryList from './galleryList.vue';
    import galleryAutoplayToggle from './galleryAutoplayToggle.vue';
    import galleryModal from './galleryModal.vue';
    import galleryLayoutModeToggle from './galleryLayoutModeToggle.vue';

    import { bus } from '../_event-bus';

    import { mapState, mapActions, mapMutations } from 'vuex';

    export default {
        name: 'ImageVideoGallery',
        data() {
            return {
            }
        },
        mounted() {
            this.checkSiteType();
            this.checkHotelName();

            this.getGalleryData().then(response => {
                setTimeout(() => {
                    bus.$emit('ListingItemsChanged');
                }, 25);
            }).catch(error => {
                console.error(error);
            });

            this.checkCMSModeToApplyFixes();

            this.setupPageVisibilityEventChange();
        },
        computed: {
            ...mapState('gallery', [
                'showFilterControls',
                'filterEnabledCount',
                'activeItem',
                'loading',
                'galleryVersion',
                'layoutMode',
            ]),

            ...mapState('translations', [
                'labels',
            ]),

            ...mapState('debug', [
                'useHardcodedData',
                'showDebugControls',
            ]),

            pageWidth() {
                return window.innerWidth;
            }
        },
        methods: {
            triggerRecalculateHeight() {
                bus.$emit('ListingItemsChanged');
            },

            addNewItem() {
                this.populateNewTestItem();
                this.triggerRecalculateHeight();
            },

            addFiveNewItems() {
                this.populateNewTestItem();
                this.populateNewTestItem();
                this.populateNewTestItem();
                this.populateNewTestItem();
                this.populateNewTestItem();
                this.triggerRecalculateHeight();
            },

            clearStorage() {
                sessionStorage.clear();
            },
            checkSiteType() {
                const siteType = document.body.getAttribute('data-site-type');
                if (siteType === null || typeof siteType === 'undefined') throw new Error('Site type is not set');

                this.updateSiteType(siteType);
            },

            checkHotelName() {
                const hotelName = document.querySelector('[data-hotel-name]');
                if (hotelName === null || typeof hotelName === 'undefined') return false;

                this.updateHotelName(hotelName.getAttribute('data-hotel-name'));
            },

            processGalleryData(data) {
                // Check which items have videos
                data.items = data.items.map(item => this.checkIfItemHasVideo(item));

                return data;
            },

            setupPageVisibilityEventChange() {
                // This is used to fire an event to components when the page becomes visibile again (after changing tabs etc).
                let hidden, visibilityChange;

                // Browser cross support checks for hidden
                if (typeof document.hidden !== "undefined") {
                    hidden = "hidden";
                    visibilityChange = "visibilitychange";
                } else if (typeof document.msHidden !== "undefined") {
                    hidden = "msHidden";
                    visibilityChange = "msvisibilitychange";
                } else if (typeof document.webkitHidden !== "undefined") {
                    hidden = "webkitHidden";
                    visibilityChange = "webkitvisibilitychange";
                }

                // Handle page visibility change   
                document.addEventListener(visibilityChange, (event) => {
                    if (document[hidden] == false) { // If document is now visible  
                        bus.$emit('PageVisibleAgain');

                        setTimeout(() => {
                            bus.$emit('PageVisibleAgain');
                        }, 150); 
                    }
                }, false);
            },

            checkCMSModeToApplyFixes() {
                // Check whether page is in preview mode or not
                const previewModeCheckElem = document.querySelector("div[data-page-in-preview-mode]");
                const isPreviewMode = previewModeCheckElem.getAttribute('data-page-in-preview-mode') === 'true' ? true : false;
                this.updateCMSPreviewMode(isPreviewMode);

                if (isPreviewMode) {
                    previewModeCheckElem.parentElement?.classList.remove('container', 'container-fluid');
                    setTimeout(() => {
                        document.body.style.overflow = '';

                        // if (this.galleryVersion === 'legacy') {
                            document.documentElement.style.overflow = '';
                        // }
                    }, 15);

                    setTimeout(() => {
                        document.body.style.overflow = '';

                        if (this.galleryVersion === 'legacy') {
                            document.documentElement.style.overflow = 'hidden';
                        }
                    }, 1750);
                }
            },

            ...mapMutations('debug', [
                'toggleBorders',
                'toggleDebugData',
                'toggleVideoElems',
                'toggleHardcodedData',
            ]),

            ...mapActions('gallery', [
                'getGalleryData',
            ]),

            ...mapMutations('gallery', [
                'updateSiteType',
                'updateHotelName',
                'populateGalleyItems',
                'populateCategories',
                'populateShowFilterControls',
                'populateNewTestItem',
                'toggleLayoutMode',
                'toggleGalleryVersion',
                'updateCMSPreviewMode',
            ]),

            ...mapMutations('translations', [
                'populateTranslationLabels',
            ]),
        },
        components: {
            galleryFilter,
            galleryList,
            galleryAutoplayToggle,
            galleryModal,
            galleryLayoutModeToggle,
        }
    }
</script>
